/**
 * Shared presentation helpers for the Files tab (TASK-818).
 *
 * Lifted out of WorkbenchFilesTab when the flat lists were split into per-client groups —
 * the group component and the tab both need to render a format icon, a date and a review
 * summary the same way, and two copies of that would drift.
 */

import React from 'react';
import { FileText, FileSpreadsheet, Presentation } from 'lucide-react';

import type { WorkbenchDocumentSummary } from '../../../services/api/documentApi';

export const FORMAT_ICON: Record<string, React.ReactNode> = {
  docx: <FileText className="h-4 w-4 text-blue-600" />,
  xlsx: <FileSpreadsheet className="h-4 w-4 text-green-600" />,
  pptx: <Presentation className="h-4 w-4 text-orange-600" />,
  pdf: <FileText className="h-4 w-4 text-red-600" />,
};

export const formatIcon = (format?: string | null): React.ReactNode =>
  FORMAT_ICON[(format || '').toLowerCase()] ?? FORMAT_ICON.docx;

export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: 'short' });
  } catch {
    return '—';
  }
};

// TASK-736: "Approved in 2 tasks · 1 pending" — from the server's
// document_review_state aggregates at current_version, never re-derived.
const REVIEW_STATE_LABEL: Record<string, string> = {
  in_review: 'In review',
  approved: 'Approved',
  changes_requested: 'Changes requested',
};

export const reviewSummary = (d: WorkbenchDocumentSummary): string | null => {
  const parts: string[] = [];
  if (d.review_document_state) {
    parts.push(REVIEW_STATE_LABEL[d.review_document_state] ?? d.review_document_state);
  }
  const approved = d.review_tasks_approved ?? 0;
  const pending = d.review_tasks_in_review ?? 0;
  const changes = d.review_tasks_changes_requested ?? 0;
  if (approved) parts.push(`Approved in ${approved} task${approved === 1 ? '' : 's'}`);
  if (pending) parts.push(`${pending} pending`);
  if (changes) parts.push(`${changes} changes requested`);
  return parts.length ? parts.join(' · ') : null;
};

/** Two-letter monogram for the client header. "No client assigned" gets a dash. */
export const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '—';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};
